import React, { useCallback } from "react";
import { IoArrowForwardCircleSharp, IoStopCircleSharp } from "react-icons/io5";
import { useChatHook } from "../../hooks/useChatHook";
import { useChatInput } from "../../hooks/chat/useChatInput";
import { useChatScroll } from "../../hooks/chat/useChatScroll";
import { VscDebugRestart } from "react-icons/vsc";
import { IconButton } from "@mui/material";
import AlertComponent from "../common/AlertComponent";
import Loader from "../common/Loader";
import MessageDisplay from "./MessageDisplay/MessageDisplay";
import "./Chat.css";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGlobalContext } from "../../context/ConversationsProvider";
import { useNavigate } from "react-router-dom";
import { useClient } from "../../context/ClientsProvider";
import { useAuth } from "../../context/AuthProvider";
import PredefinedInput from "./PredefinedInput";
import { Card, Pill } from "../../design-system/components";

const suggestions = [
  "What can I ask about?",
  "Show me a summary of my lease portfolio",
  "What leases are expiring in the next 12 months?",
];


function StoreStatus({ storeStatus, isStreaming }) {
  if (storeStatus.loading) {
    return (
      <div className="message-status">
        <p>Saving...</p>
      </div>
    );
  }
  if (isStreaming) {
    return (
      <div className="message-status">
        <p>Please wait, we are generating a response.</p>
      </div>
    );
  }
  if (storeStatus.success) {
    return (
      <div className="message-status">
        <p> Synced with server</p>
      </div>
    );
  }
  return <></>;
}

export function NoConversation({ onSuggestionClick, disabled = false, showSuggestions = true }) {
  const { authState } = useAuth();
  // Okta OIDC userinfo: prefer given_name, fall back to the first word
  // of the full name; greet namelessly when neither is present.
  const user = authState?.user;
  const firstName = user?.given_name || (user?.name || "").split(" ")[0] || "";

  return (
    <div className="new-chat--wrapper">
      <div className="new-chat--header">
        <div className="copilot-logo--wrapper">
          <img src="/logo.svg" alt="" />
        </div>
        <p className="new-chat-greeting">Hello{firstName ? `, ${firstName}` : ""}!</p>
        <div className="new-chat-divider" aria-hidden="true" />
        <h1>How can Savills Copilot help you today?</h1>
      </div>
      {showSuggestions && (
        <Card padded className="suggestions--wrapper">
          {suggestions.map((question) => (
            <Pill
              key={question}
              interactive
              disabled={disabled}
              onClick={() => onSuggestionClick(question)}
            >
              {question}
            </Pill>
          ))}
        </Card>
      )}
    </div>
  );
}

const Chat = () => {
  const navigate = useNavigate();
  const {
    activeConversation,
    conversationId,
    isStreaming,
    cancelStreaming,
    sendMessage,
    regenerate,
    createConversation,
    isCreatingConversation,
    isLoadingConversation,
    storeStatus,
    error,
    dismissError,
  } = useChatHook();
  // Input + scroll state are owned by Chat now (Phase 4): they never
  // escape this component, so threading them through useChatHook just
  // muddied its surface.
  const { inputContent, setInputContent, inputRef } = useChatInput();
  const { messagesEndRef } = useChatScroll({ trigger: isStreaming });

  const { selectedClient } = useClient();

  const { dataErrors } = useGlobalContext();
  const matches = useMediaQuery("(max-width:768px)");
  const chatErrorMessage =
    dataErrors?.error ||
    dataErrors?.data?.error ||
    (typeof dataErrors === "string" ? dataErrors : null);
  const chatDisabled = !!chatErrorMessage;

  const isPredefinedMode = selectedClient?.chatMode === 'predefined';
  const predefinedQuestions = selectedClient?.predefinedQuestions || [];

  const onNewMessage = useCallback(
    async (text) => {
      if (chatDisabled) return;
      // Clear the input immediately on submit/suggestion-click. The
      // hook used to do this internally; lifting it here lets the
      // input state stay scoped to Chat.jsx.
      setInputContent("");

      if (!conversationId) {
        const newConversationId = await createConversation(text);
        // Carry the active client in the path so a refresh on a freshly
        // created conversation restores the matching client.
        navigate(
          `/${encodeURIComponent(selectedClient.id)}/c/${newConversationId}`,
        );
        await sendMessage(newConversationId, text);
      } else {
        await sendMessage(conversationId, text);
      }
    },
    [
      chatDisabled,
      conversationId,
      createConversation,
      sendMessage,
      navigate,
      setInputContent,
      selectedClient,
    ],
  );

  return (
    <>
      <div
        className={`chat--container${chatDisabled ? " disabled" : ""}`}
      >
        <StoreStatus storeStatus={storeStatus} isStreaming={isStreaming} />

        <div className="main-chat--container">
          {!!conversationId ? (
            <>
              {activeConversation.messages.map((message, index) => (
                <MessageDisplay
                  // Prefer the stable client-side ID so React's reconciler
                  // doesn't lose track of a message when its position
                  // shifts (e.g. a regenerate that drops the trailing
                  // assistant placeholder). Server-loaded messages and
                  // the greeting fall back to index — they don't move.
                  key={message.clientMessageId ?? index}
                  message={message}
                  index={index}
                  isStreaming={isStreaming}
                  clientId={selectedClient?.id}
                  conversationId={conversationId}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          ) : (
            <NoConversation
              disabled={chatDisabled}
              onSuggestionClick={(text) => onNewMessage(text)}
              showSuggestions={!isPredefinedMode}
            />
          )}
        </div>

        <div className="prompt-input--container">
          {chatErrorMessage && (
            <p className="chat-error-message">{chatErrorMessage}</p>
          )}
          {activeConversation.networkErrorActive ? (
            <div className="regenerate--container">
              <p className="regenerate-message">
                There was a network error, please regenerate.
              </p>
              <button onClick={regenerate}>
                <span className="btn-icon">
                  <VscDebugRestart />
                </span>
                <span>Regenerate</span>
              </button>
            </div>
          ) : (
            <form
              className="input--wrapper"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!isPredefinedMode) {
                  await onNewMessage(inputContent);
                }
              }}
            >
              {isPredefinedMode ? (
                <PredefinedInput
                  questions={predefinedQuestions}
                  onQuestionClick={onNewMessage}
                  disabled={chatDisabled}
                />
              ) : (
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Ask anything..."
                  value={inputContent}
                  disabled={chatDisabled}
                  onChange={(e) => setInputContent(e.target.value)}
                />
              )}
              {!isPredefinedMode && (
                <div className="input-send-icon--wrapper">
                  {isStreaming ? (
                    <IconButton
                      sx={{ borderRadius: "100%" }}
                      type="button"
                      size={matches ? "small" : "medium"}
                      onClick={(e) => {
                        e.preventDefault();
                        cancelStreaming();
                      }}
                      aria-label="Stop generating"
                      title="Stop generating"
                    >
                      <IoStopCircleSharp className="input-send-icon" />
                    </IconButton>
                  ) : (
                    <IconButton
                      sx={{ borderRadius: "100%" }}
                      type="submit"
                      size={matches ? "small" : "medium"}
                      disabled={
                        isStreaming ||
                        isLoadingConversation ||
                        isCreatingConversation ||
                        chatDisabled
                      }
                    >
                      <IoArrowForwardCircleSharp className="input-send-icon" />
                    </IconButton>
                  )}
                </div>
              )}
            </form>
          )}

          <div className="copyright--wrapper">
            <p className="copyright-text">
              <span className="copyright-logo">
                <img src="/assets/grayLogo.svg" alt="" />
              </span>
                  All your questions are powered by the Savills Portfolio Assistant data system
            </p>
          </div>
        </div>
      </div>

      {(isLoadingConversation || isCreatingConversation) && (
        <Loader />
      )}
      <AlertComponent
        open={Boolean(error)}
        setOpen={(open) => {
          if (!open) dismissError();
        }}
        type="error"
        message={error?.message ?? ""}
        position={{ vertical: "top", horizontal: "center" }}
        reset={dismissError}
      />
    </>
  );
};

export default Chat;
